import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import OtpInput from 'react-otp-input';
import { userApi } from '../../api/user.api';
import { authApi } from '../../api/auth.api';
import { useUI } from '../../store/UIContext';
import { useAuth } from '../../store/AuthContext';
import { PageHeader, Btn, PasswordField } from '../../components/ui/index';
import { toPersian } from '../../utils/persianNumber';
import { phoneError, emailError, normalizeIranianMobile, phoneInputChange, phoneInputPaste } from '../../utils/validators';
import { composeCompanyUsername, DEFAULT_USERNAME_SPLITTER } from '../../utils/usernamePrefix';

const CODE_LEN = 7;

// add_company_user / add_department_employee only stage the new user and
// text them an OTP (see queryAddNewComapanyUser/queryAddNewDepartmentEmployee
// in Authentication.py) - nothing is written to the database until that code
// is verified via register_info. This page previously stopped after the
// staging call and reported success immediately, so no user was ever
// actually created.
export default function AddUserPage() {
  const navigate = useNavigate();
  const location = useLocation();
  // The routes are path-based (/users/add/employee vs /users/add/company),
  // not ?type=employee - reading from useSearchParams() here always fell
  // through to the 'company' default regardless of which link was clicked,
  // so a manager_employee adding an employee silently hit add_company_user
  // (manager_user-only) instead of add_department_employee and got a 403.
  const type = location.pathname.includes('/employee') ? 'employee' : 'company';
  const { addToast } = useUI();
  const { user } = useAuth() || {};
  const [step, setStep] = useState('form'); // 'form' | 'verify'
  const [form, setForm] = useState({ username:'', password:'', password2:'', email:'', phone:'' });
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [splitter, setSplitter] = useState(DEFAULT_USERNAME_SPLITTER);
  const inp = { width:'100%', padding:'10px 14px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none', fontFamily:'inherit', boxSizing:'border-box' };
  const set = k => e => setForm(p => ({ ...p, [k]: e.target.value }));
  const phoneErr = phoneError(form.phone);
  const emailErr = emailError(form.email);
  const pwMismatch = !!form.password2 && form.password !== form.password2;
  const companyName = user?.company_name || '';
  const composedUsername = type === 'company'
    ? composeCompanyUsername(companyName, form.username, splitter)
    : form.username.trim();

  useEffect(() => {
    if (type !== 'company') return;
    userApi.getUsernamePrefixSettings()
      .then((res) => setSplitter(res.data?.splitter || DEFAULT_USERNAME_SPLITTER))
      .catch(() => {});
  }, [type]);

  const submit = async () => {
    if (phoneErr || emailErr || form.password !== form.password2) return;
    setLoading(true);
    try {
      // Backend fields are username/password/phone_number/image_data -
      // 'email' isn't read by either endpoint, phone_number (not 'phone')
      // is required, and image_data is always empty (no upload UI here).
      const payload = {
        username: form.username,
        password: form.password,
        phone_number: normalizeIranianMobile(form.phone),
        image_data: '',
      };
      const res = type==='employee' ? await userApi.addDepartmentEmployee(payload) : await userApi.addCompanyUser(payload);
      // The backend also sets this via Set-Cookie (department_employee_
      // session_id / company_user_session_id), but that's a cross-origin
      // Set-Cookie from crm.pejvakrayane.com that some browsers silently
      // refuse to store (third-party cookie blocking) - the follow-up
      // verify() call then has no cookie to read and 501s. Mirror
      // authApi.login/SignupPage's fallback: write it via document.cookie
      // directly from the response body, which is always available.
      if (res.data?.session_id) {
        const cookieName = type === 'employee' ? 'department_employee_session_id' : 'company_user_session_id';
        document.cookie = `${cookieName}=${res.data.session_id}; path=/;`;
      }
      addToast('کد تایید به موبایل کاربر ارسال شد', 'success');
      setStep('verify');
    } catch { addToast('خطا در افزودن کاربر', 'error'); }
    setLoading(false);
  };

  const verify = async () => {
    if (code.length < CODE_LEN) { addToast('کد را کامل وارد کنید', 'error'); return; }
    setLoading(true);
    try {
      await authApi.registerInfo({ auth_code: code, type: 'phone', self_registration: 0 });
      addToast('کاربر افزوده شد', 'success');
      navigate('/users');
    } catch { addToast('کد وارد شده صحیح یا منقضی شده است', 'error'); }
    setLoading(false);
  };

  return (
    <div className="fade-in">
      <PageHeader title={type==='employee'?'افزودن کارمند':'افزودن کاربر'}/>
      <div style={{maxWidth:520,background:'#fff',borderRadius:16,border:'1px solid rgba(35,58,122,0.1)',padding:32,boxShadow:'0 4px 16px rgba(35,58,122,0.08)'}}>
        {step === 'form' ? (
          <div style={{display:'flex',flexDirection:'column',gap:14}}>
            {[['username','نام کاربری *','text'],['password','رمز عبور *','password'],['email','ایمیل','email'],['phone','موبایل *','tel']].map(([k,lbl,t])=>{
              const err = k === 'phone' ? phoneErr : k === 'email' ? emailErr : '';
              if (k === 'password') {
                const lab = {fontSize:12,fontWeight:600,color:'#334155'};
                return (
                  <div key={k} style={{display:'flex',flexDirection:'column',gap:14}}>
                    <PasswordField label="رمز عبور *" labelStyle={lab} value={form.password} onChange={set('password')} />
                    <PasswordField label="تکرار رمز عبور *" labelStyle={lab} value={form.password2} onChange={set('password2')} error={pwMismatch ? 'تکرار رمز عبور با رمز عبور یکسان نیست' : ''} />
                  </div>
                );
              }
              const onFieldChange = k === 'phone' ? phoneInputChange(set(k)) : set(k);
              return (
                <div key={k}>
                  <label style={{fontSize:12,fontWeight:600,color:'#334155',display:'block',marginBottom:5}}>{lbl}</label>
                  <input type={t} value={form[k]} onChange={onFieldChange} onPaste={k === 'phone' ? phoneInputPaste(set(k)) : undefined} inputMode={k === 'phone' ? 'numeric' : undefined} maxLength={k === 'phone' ? 11 : undefined} style={{...inp,direction:t==='email'||t==='tel'?'ltr':'rtl',borderColor: err ? '#fca5a5' : '#e2e8f0'}}/>
                  {k === 'username' && type === 'company' && composedUsername && (
                    <p dir="ltr" style={{fontSize:12,color:'#64748b',margin:'6px 0 0',wordBreak:'break-all'}}>
                      نام کاربری نهایی: {composedUsername}
                    </p>
                  )}
                  {err && <p style={{color:'#dc2626',fontSize:11,margin:'5px 0 0'}}>{err}</p>}
                </div>
              );
            })}
            <div style={{display:'flex',gap:10,marginTop:8}}>
              <Btn variant="ghost" onClick={()=>navigate('/users')}>انصراف</Btn>
              <Btn onClick={submit} loading={loading} disabled={!form.username.trim() || !form.password.trim() || form.password !== form.password2 || !form.phone.trim() || !!phoneErr || !!emailErr}>ذخیره</Btn>
            </div>
          </div>
        ) : (
          <div style={{display:'flex',flexDirection:'column',gap:16,alignItems:'center',textAlign:'center'}}>
            <p style={{fontSize:13,color:'#64748b',margin:0}}>
              کد {toPersian(CODE_LEN)} رقمی ارسال‌شده به موبایل «{form.phone}» را وارد کنید
            </p>
            <OtpInput
              value={code}
              onChange={setCode}
              numInputs={CODE_LEN}
              shouldAutoFocus
              renderInput={(props) => <input {...props} disabled={loading} />}
              inputStyle={{ width: '2.2em', height: '2.4em', margin: '0 4px', fontSize: 18, borderRadius: 8, border: '1px solid #e2e8f0', textAlign: 'center' }}
              containerStyle={{ direction: 'ltr' }}
            />
            <div style={{display:'flex',gap:10,marginTop:8,width:'100%'}}>
              <Btn variant="ghost" onClick={()=>setStep('form')} style={{flex:1}}>بازگشت</Btn>
              <Btn onClick={verify} loading={loading} disabled={code.length<CODE_LEN} style={{flex:1}}>تایید کد</Btn>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
